import { useEffect, useState } from "react";
import { Box, HStack, Tooltip } from "@chakra-ui/react";
import { FiClock } from "react-icons/fi";
import CDS from "design-system";

interface ReservationCountdownProps {
  expiresAt: string;
  onExpired: () => void;
}

function formatRemaining(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

function getNow(): number {
  return Date.now();
}

export default function ReservationCountdown({ expiresAt, onExpired }: ReservationCountdownProps) {
  const [now, setNow] = useState(getNow);

  useEffect(() => {
    const interval = setInterval(() => setNow(getNow()), 1000);
    return () => clearInterval(interval);
  }, []);

  const remaining = Math.max(0, Math.floor((new Date(expiresAt).getTime() - now) / 1000));

  const expired = remaining <= 0;
  useEffect(() => {
    if (expired) onExpired();
  }, [expired, onExpired]);

  const isUrgent = remaining <= 120;
  const colorScheme = isUrgent ? "orange" : "gray";

  return (
    <Tooltip
      label="Calendars change constantly, so we keep these time slots held for a limited window. When it expires we'll find fresh times automatically."
      placement="bottom"
      hasArrow
      maxW="280px"
    >
      <Box
        display="inline-flex"
        bg={`${colorScheme}.50`}
        borderWidth="1px"
        borderColor={`${colorScheme}.200`}
        borderRadius="md"
        px={2.5}
        py={1}
        cursor="default"
      >
        <HStack spacing={1.5}>
          <Box color={`${colorScheme}.500`}>
            <FiClock size={14} />
          </Box>
          <CDS.Text10 fontWeight="medium" color={`${colorScheme}.700`} fontVariantNumeric="tabular-nums">
            {remaining > 0 ? `Held for ${formatRemaining(remaining)}` : "Refreshing times..."}
          </CDS.Text10>
        </HStack>
      </Box>
    </Tooltip>
  );
}
